'use client'

import React, { FC } from 'react'
import Link from 'next/link'
import { TableCell } from '@/components/ui/table'
import { Collection } from '@/utils/types/collections'
import useWindowSize from '@/utils/hooks/useWindowSize'
import PercentChange from '@/components/percent-change'
import SaleChange from '@/components/market-sale-change'
import { fairDayChange, indexedSaleChange } from '@/utils/market-metrics'
import { lowerQuote } from '@/lib/quotes/hooks'
import { CollectionsFeedFilters } from '@/utils/types/feed'
import useSatoshiPrice from '@/utils/hooks/useSatoshiPrice'
import BitconOrangeIcon from '@/icons/bitcoin-filled'
import CollectionIcon from '@/components/collection-icon'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import numberWithCommas from '@/utils/format-number'
import isFungible from '@/utils/is-fungible'
import { formatSatsPer } from '@/utils/format-sats'
import { formatUsdPerBillion } from '@/utils/format-usd'

// Hoisted to module scope so it's not redefined every render of CollectionRow.
// Defining it inside the parent created a fresh component identity each render
// — which destroyed local state and confused React reconciliation
// (no-nested-component-definition).
const PricePreview: FC<{
	exchangeRate: number
	hideIcon?: boolean
	children: React.ReactNode
}> = ({ exchangeRate, hideIcon = false, children }) => {
	const btcValue = parseFloat(children as string)
	const usdValue = btcValue * exchangeRate
	const showUsd = usdValue >= 0.0000001

	return (
		<div className="inline-flex flex-col items-end">
			<div className="flex items-center gap-1">
				{!hideIcon && <BitconOrangeIcon className="size-3.5 min-w-3.5 fill-[#F7931A]" />}
				<span className="text-xs">{children}</span>
			</div>
			{showUsd && (
				<div className="text-xs text-muted-foreground">
					${numberWithCommas((btcValue * exchangeRate).toFixed(2))}
				</div>
			)}
		</div>
	)
}

const FungiblePricePreview: FC<{ satsPerToken?: number; exchangeRate: number }> = ({
	satsPerToken,
	exchangeRate
}) => {
	if (!satsPerToken || satsPerToken <= 0) {
		return (
			<div className="inline-flex flex-col items-end">
				<div className="flex items-center gap-1">
					<BitconOrangeIcon className="size-3.5 min-w-3.5 fill-[#F7931A]" />
					<span className="text-xs">0</span>
				</div>
			</div>
		)
	}

	const { sats, per } = formatSatsPer(satsPerToken)
	const showBtcIcon = per !== 'sats'
	const usdPerUnit = (satsPerToken / 1e8) * exchangeRate
	const usdLabel =
		usdPerUnit >= 0.01
			? `$${usdPerUnit.toLocaleString('en-US', {
					maximumFractionDigits: 2,
					minimumFractionDigits: 2
			  })}`
			: formatUsdPerBillion(usdPerUnit)

	return (
		<div className="inline-flex flex-col items-end">
			<div className="flex items-center gap-1">
				{showBtcIcon && <BitconOrangeIcon className="size-3.5 min-w-3.5 fill-[#F7931A]" />}
				<span className="text-xs">
					{sats}
					{!showBtcIcon && <span className="text-muted-foreground ml-0.5">sats</span>}
				</span>
			</div>
			{usdLabel && <div className="text-xs text-muted-foreground">{usdLabel}</div>}
		</div>
	)
}

const CollectionRow: FC<{
	rank?: number
	selectedKey: CollectionsFeedFilters
	collection: Collection
	selectedTitle?: string
}> = ({ collection, rank: _rank, selectedKey, selectedTitle }) => {
	const { isMobile } = useWindowSize()
	const { exchangeRate } = useExchangeRate()

	const value = (
		{
			VolumeHourDesc: (collection['volume_hour'] / 1e8)?.toFixed(8),
			VolumeSixHourDesc: (collection['volume_six_hour'] / 1e8)?.toFixed(8),
			VolumeDayDesc: (collection['volume_day'] / 1e8)?.toFixed(8),
			VolumeWeekDesc: (collection['volume_week'] / 1e8)?.toFixed(8),
			VolumeMonthDesc: (collection['volume_month'] / 1e8)?.toFixed(8),
			VolumeTotalDesc: (collection['volume_all_time'] / 1e8)?.toFixed(8),
			GlobalVolumeDayDesc: (((collection as any)?.global_volume_day ?? 0) / 1e8)?.toFixed(8),
			GlobalVolumeWeekDesc: (((collection as any)?.global_volume_week ?? 0) / 1e8)?.toFixed(8),
			GlobalVolumeTotalDesc: (((collection as any)?.global_volume_all_time ?? 0) / 1e8)?.toFixed(8),
			ChangeWeekDesc: collection?.change_week,
			PriceChangeDayDesc: fairDayChange(collection),
			FloorAsc: ((collection as any)?.floor_price / 1e8)?.toFixed(8),
			FloorDesc: ((collection as any)?.floor_price / 1e8)?.toFixed(8),
			OwnersDesc: collection?.owners
		} as any
	)[selectedKey]

	return (
		<>
			<TableCell className="py-1.5 pr-4 group-hover:bg-neutral-800">
				<Link href={`/collection/${collection?.slug}`} className="hover:no-underline">
					<div className="flex items-center gap-2">
						<div className="relative size-7 overflow-hidden rounded-md flex-shrink-0">
							<CollectionIcon
								collection={collection}
								className="size-full"
								imageClassName="rounded-md"
							/>
						</div>
						<div className="flex flex-col max-w-[100px] sm:max-w-full">
							<span translate="no" className="font-medium text-xs leading-tight truncate">{collection?.name}</span>
							{collection?.sponsored && (
								<span className="text-[10px] leading-tight font-semibold text-purple-500 truncate">
									Sponsored
								</span>
							)}
						</div>
					</div>
				</Link>
			</TableCell>
			<TableCell className="text-right py-1.5 pr-4 group-hover:bg-neutral-800">
				{['VolumeWeekDesc', 'GlobalVolumeWeekDesc', 'VolumeTotalDesc'].includes(selectedKey) ? (
                    <SaleChange slug={collection.slug} existing={indexedSaleChange(collection)} fungible={Boolean(isFungible(collection.slug))} />
                ) : selectedKey === 'ChangeWeekDesc' ? (
                    <PercentChange value={collection.change_week} className="text-xs" />
                ) : selectedTitle?.includes('Change') ||
				['Volume 7d', 'Global Vol 7d', 'Total Volume'].includes(selectedTitle || '') ? (
					<PercentChange
						value={
							selectedTitle?.includes('24h')
								? fairDayChange(collection)
								: indexedSaleChange(collection)
						}
						className="text-xs"
					/>
				) : selectedTitle?.includes('Floor') ? (
					<span className="text-xs">{value}</span>
				) : (
					<PricePreview
						exchangeRate={exchangeRate}
						hideIcon={isMobile && selectedTitle?.includes('Change')}
					>
						{value || 0}
					</PricePreview>
				)}
			</TableCell>
			{/* Global (cross-marketplace) volume — all-time, always populated for
			    indexed collections. Sits beside the OW column so both show. */}
			<TableCell className="text-right py-1.5 pr-4 group-hover:bg-neutral-800">
				<PricePreview exchangeRate={exchangeRate}>
					{((((selectedKey.includes('Week') ? collection.global_volume_week : selectedKey.includes('Day') ? collection.global_volume_day : collection.global_volume_all_time) ?? 0) as number) / 1e8)?.toFixed(8)}
				</PricePreview>
			</TableCell>
			{/* OW (Ordinals Wallet) marketplace volume — all-time. */}
			<TableCell className="text-right py-1.5 pr-4 group-hover:bg-neutral-800">
				<PricePreview exchangeRate={exchangeRate}>
					{useSatoshiPrice(selectedKey.includes('Week') ? collection.volume_week : selectedKey.includes('Day') ? collection.volume_day : collection.volume_all_time) || 0}
				</PricePreview>
			</TableCell>
			<TableCell className="text-right py-1.5 pr-4 group-hover:bg-neutral-800">
				{/* What one is worth: fair price or floor, whichever is lower (per
				    token for fungibles) -- the wallet's and listing flows' rule. */}
				{isFungible(collection?.slug) ? (
					<FungiblePricePreview
						satsPerToken={
							lowerQuote(
								(collection as any)?.fair_sats,
								Number(collection?.floor_price_per ?? collection?.floor_price) || null
							) ?? undefined
						}
						exchangeRate={exchangeRate}
					/>
				) : (
					<PricePreview exchangeRate={exchangeRate}>
						{((lowerQuote((collection as any)?.fair_sats, Number((collection as any)?.floor_price) || null) ?? 0) / 1e8)?.toFixed(8) || 0}
					</PricePreview>
				)}
			</TableCell>
		</>
	)
}

export default CollectionRow
